import React from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  ArrowLeft,
  TrendingUp, 
  Clock, 
  CheckCircle, 
  Mail, 
  MapPin,
  Zap,
  Target,
  BarChart3,
  Users,
  Building2,
  Calendar
} from "lucide-react";

const CaseStudies = () => {
  const caseStudies = [
    {
      id: 1,
      company: "Premier Medical Group",
      industry: "Healthcare",
      challenge: "Manual patient scheduling and follow-up processes consuming 25+ hours weekly",
      solution: "Automated appointment scheduling, patient reminders, and follow-up workflows",
      results: {
        timeSaved: "28 hours/week",
        efficiency: "85% faster",
        satisfaction: "40% increase"
      },
      metrics: [
        { label: "Time Saved Weekly", value: "28 hours", icon: Clock },
        { label: "Process Speed Increase", value: "85%", icon: TrendingUp },
        { label: "Patient Satisfaction", value: "+40%", icon: Users }
      ],
      category: "Healthcare"
    },
    {
      id: 2,
      company: "Thompson & Associates CPA",
      industry: "Financial Services", 
      challenge: "Invoice processing and client communication bottlenecks during tax season",
      solution: "Automated invoice processing, client portal, and document management system",
      results: {
        timeSaved: "35 hours/week",
        efficiency: "70% faster", 
        satisfaction: "60% reduction"
      },
      metrics: [
        { label: "Weekly Time Savings", value: "35 hours", icon: Clock },
        { label: "Processing Speed", value: "70%", icon: BarChart3 },
        { label: "Error Reduction", value: "60%", icon: Target }
      ],
      category: "Finance"
    },
    {
      id: 3,
      company: "Valley Construction Co.",
      industry: "Construction",
      challenge: "Project coordination and client updates handled manually across multiple sites",
      solution: "Automated project tracking, client notifications, and resource management",
      results: {
        timeSaved: "22 hours/week",
        efficiency: "65% faster",
        satisfaction: "50% improvement"
      },
      metrics: [
        { label: "Administrative Time", value: "22 hours", icon: Clock },
        { label: "Project Coordination", value: "65%", icon: Building2 },
        { label: "Client Communication", value: "+50%", icon: CheckCircle }
      ],
      category: "Construction"
    }
  ];

  return (
    <div style={{ background: "var(--bg-page)", minHeight: "100vh" }}>
      {/* Header */}
      <header className="header-nav">
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div className="heading-2 logo">
              MAIN STREET SYSTEMS
            </div>
            <div className="nav-actions">
              <Button variant="ghost" className="btn-secondary" onClick={() => window.location.hash = '#/'}>
                <ArrowLeft size={16} style={{ marginRight: "0.5rem" }} />
                Back to Home
              </Button>
              <Button className="btn-primary">
                Book Consultation
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: "60vh", paddingTop: "8rem" }}>
        <div className="hero-content">
          <div className="hero-announcement">
            <Target size={16} />
            <span>Real Results for Real Businesses</span>
          </div>
          <h1 className="heading-hero" style={{ marginBottom: "1rem" }}>
            Case Studies
          </h1>
          <p className="body-large" style={{ marginBottom: "2rem", maxWidth: "700px", marginLeft: "auto", marginRight: "auto" }}>
            See how we've helped Main Street businesses transform their operations and reclaim valuable time through intelligent automation solutions.
          </p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="pad-2xl">
        <div className="container">
          <div className="ai-grid" style={{ gap: "2rem" }}>
            {caseStudies.map((study) => (
              <Card key={study.id} className="voice-card accent-blue" style={{ padding: "2rem", height: "auto" }}>
                <div style={{ marginBottom: "1.5rem" }}>
                  <Badge style={{ 
                    background: "var(--accent-primary)", 
                    color: "#000000", 
                    marginBottom: "1rem",
                    fontSize: "0.75rem",
                    fontWeight: "600"
                  }}>
                    {study.category}
                  </Badge>
                  <h3 className="heading-2" style={{ marginBottom: "0.5rem" }}>{study.company}</h3>
                  <p className="body-small" style={{ color: "var(--text-muted)", marginBottom: "1rem" }}>{study.industry}</p>
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <h4 className="heading-3" style={{ marginBottom: "0.75rem", color: "var(--text-primary)" }}>Challenge</h4>
                  <p className="body-small" style={{ lineHeight: "1.6" }}>{study.challenge}</p>
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <h4 className="heading-3" style={{ marginBottom: "0.75rem", color: "var(--text-primary)" }}>Solution</h4>
                  <p className="body-small" style={{ lineHeight: "1.6" }}>{study.solution}</p>
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <h4 className="heading-3" style={{ marginBottom: "1rem", color: "var(--accent-primary)" }}>Results</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.75rem" }}>
                    {study.metrics.map((metric, index) => {
                      const IconComponent = metric.icon;
                      return (
                        <div key={index} style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "0.75rem",
                          padding: "0.75rem",
                          background: "rgba(0, 255, 136, 0.1)",
                          borderRadius: "0.5rem",
                          border: "1px solid rgba(0, 255, 136, 0.2)"
                        }}>
                          <IconComponent size={20} style={{ color: "var(--accent-primary)" }} />
                          <div>
                            <div style={{ 
                              fontSize: "1.25rem", 
                              fontWeight: "700", 
                              color: "var(--accent-primary)",
                              marginBottom: "0.25rem"
                            }}>
                              {metric.value}
                            </div>
                            <div className="caption" style={{ color: "var(--text-muted)" }}>
                              {metric.label}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ 
                  borderTop: "1px solid var(--border-primary)", 
                  paddingTop: "1rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <Calendar size={20} style={{ color: "var(--accent-primary)" }} />
                    <span className="body-small" style={{ color: "var(--text-muted)" }}>
                      Implementation: 4-6 weeks
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pad-2xl" style={{ background: "var(--gradient-section)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <h2 className="heading-1" style={{ marginBottom: "1rem" }}>
              Ready to Be Our Next Success Story?
            </h2>
            <p className="body-large" style={{ maxWidth: "700px", margin: "0 auto", marginBottom: "2rem" }}>
              Join these successful small businesses and transform your operations with our proven automation solutions.
            </p>
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
              <Button className="btn-primary">
                Schedule Your Free Consultation
             </Button>
             /*  <Button className="btn-secondary">
                Download Full Case Studies
              </Button> */
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pad-lg" style={{ background: "#000000", color: "var(--text-primary)" }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="heading-2" style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>
                MAIN STREET SYSTEMS
              </div>
              <p className="body-small" style={{ color: "var(--text-secondary)" }}>
                Transforming Main Street businesses through intelligent automation solutions.
              </p>
            </div>
            
            <div>
              <h3 className="heading-3" style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>Contact</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <Mail size={16} style={{ color: "var(--accent-primary)" }} />
                  <span className="body-small" style={{ color: "var(--text-secondary)" }}>hello@mainstreetsystems.ai</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <MapPin size={16} style={{ color: "var(--accent-primary)" }} />
                  <span className="body-small" style={{ color: "var(--text-secondary)" }}>New York City</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="heading-3" style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>Services</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                <li className="body-small" style={{ color: "var(--text-secondary)", marginBottom: "0.75rem" }}>Business Process Automation</li>
                <li className="body-small" style={{ color: "var(--text-secondary)", marginBottom: "0.75rem" }}>Workflow Optimization</li>
                <li className="body-small" style={{ color: "var(--text-secondary)", marginBottom: "0.75rem" }}>Custom AI Solutions</li>
                <li className="body-small" style={{ color: "var(--text-secondary)" }}>Strategic Consulting</li>
              </ul>
            </div>
          </div>
          
          <div style={{ 
            borderTop: "1px solid var(--border-primary)", 
            paddingTop: "2rem", 
            marginTop: "3rem", 
            textAlign: "center" 
          }}>
            <p className="caption" style={{ color: "var(--text-muted)" }}>
              © 2025 Main Street Systems AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaseStudies;
